import { Injectable, signal } from '@angular/core';
import { safeVideoUrl } from '../utils/safe-url.util';

/** A YouTube video, played in the youtube-nocookie embed. */
export interface YoutubeModalItem {
  readonly kind: 'youtube';
  /** A validated 11-character YouTube ID. */
  readonly videoId: string;
  /** Accessible name for the dialog and the player. */
  readonly title: string;
}

/** A video uploaded through the CMS, played in a native <video>. */
export interface Mp4ModalItem {
  readonly kind: 'mp4';
  /** An https:// MP4 URL, already checked by safeVideoUrl(). */
  readonly url: string;
  readonly title: string;
  /** Shown until the first frame is ready. */
  readonly poster: string | null;
}

/** The video the pop-up is showing. */
export type VideoModalItem = YoutubeModalItem | Mp4ModalItem;

/**
 * Either source a CMS video can have. Exactly one is expected; a YouTube id
 * wins if a caller passes both.
 */
export interface VideoModalSource {
  readonly youtubeId?: string | null;
  readonly videoUrl?: string | null;
  readonly poster?: string | null;
}

/** Exactly the IDs parseYoutubeId() lets through. */
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

/**
 * Opens videos in the site's one video pop-up (VideoModalComponent, rendered
 * once in AppComponent): a YouTube video by its ID, or an MP4 uploaded
 * through the CMS by its URL.
 *
 * One host at the app root rather than a pop-up per card: a card can sit
 * inside a transformed or filtered ancestor (scroll reveals, carousels),
 * which would trap a `position: fixed` dialog inside that ancestor's box.
 */
@Injectable({ providedIn: 'root' })
export class VideoModalService {
  private readonly current = signal<VideoModalItem | null>(null);

  /** The open video, or null when the pop-up is closed. */
  readonly video = this.current.asReadonly();

  /**
   * Opens the pop-up on a YouTube `videoId`. The ID is checked again here, not
   * just trusted from the caller, because it ends up inside the player's
   * embed URL.
   */
  open(videoId: string | null | undefined, title: string): void {
    if (!videoId || !YOUTUBE_ID.test(videoId)) { return; }
    this.current.set({ kind: 'youtube', videoId, title });
  }

  /**
   * Opens the pop-up on an uploaded MP4. Like the ID above, the URL is checked
   * again here (https only) because it becomes the <video>'s src.
   */
  openMp4(url: string | null | undefined, title: string, poster?: string | null): void {
    const src = safeVideoUrl(url);
    if (!src) { return; }
    this.current.set({ kind: 'mp4', url: src, title, poster: (poster ?? '').trim() || null });
  }

  /** Opens whichever source the video has; does nothing when neither is usable. */
  openSource(source: VideoModalSource, title: string): void {
    if (source.youtubeId && YOUTUBE_ID.test(source.youtubeId)) {
      this.open(source.youtubeId, title);
    } else {
      this.openMp4(source.videoUrl, title, source.poster);
    }
  }

  close(): void {
    this.current.set(null);
  }
}
